import React, { useState } from "react";
import axios from "axios";
import { Instagram, Facebook, Music2, Globe, Mail, MapPin, ArrowRight } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { useReveal, revealClass } from "../hooks/useReveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SOCIAL = {
  personal: [
    { type: "ig", label: "@ivanbautistasoriano", href: "https://instagram.com/ivanbautistasoriano", icon: Instagram },
  ],
  projects: [
    { type: "ig-proxima", label: "@proximaleague", href: "https://instagram.com/proximaleague", icon: Instagram },
    { type: "web-proxima", label: "proximaleague.com", href: "https://proximaleague.com", icon: Globe },
    { type: "fb-proxima", label: "@proximaleague", href: "https://facebook.com/proximaleague", icon: Facebook },
    { type: "ig-solibrum", label: "@solibrum", href: "https://instagram.com/solibrum", icon: Instagram },
    { type: "ig-athletic", label: "@athleticgironi", href: "https://instagram.com/athleticgironi", icon: Instagram },
    { type: "tk-athletic", label: "@athleticgironi", href: "https://tiktok.com/@athleticgironi", icon: Music2 },
  ],
};

export const Contact = () => {
  const { t } = useLanguage();
  const [headRef, headVisible] = useReveal();
  const [formRef, formVisible] = useReveal();

  const [data, setData] = useState({ name: "", email: "", message: "" });
  const [status, setStatus] = useState({ state: "idle", message: "" });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setData((d) => ({ ...d, [name]: value }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!data.name || !data.email || !data.message) return;
    setStatus({ state: "sending", message: t.contact.sending });
    try {
      await axios.post(`${API}/contact`, data);
      setStatus({ state: "success", message: t.contact.success });
      setData({ name: "", email: "", message: "" });
    } catch (err) {
      console.error(err);
      setStatus({ state: "error", message: t.contact.error });
    }
  };

  return (
    <section
      id="contact"
      data-testid="section-contact"
      className="py-24 md:py-32 border-t border-line bg-white scroll-mt-24"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div
          ref={headRef}
          className={`${revealClass(headVisible)} mb-14 grid grid-cols-12 gap-y-6 md:gap-x-16`}
        >
          <div className="col-span-12 md:col-span-4">
            <p className="section-index mb-4" data-testid="contact-index">
              {t.contact.index}
            </p>
            <h2
              className="font-serif-display font-light tracking-tight text-ink text-5xl md:text-6xl leading-none"
              data-testid="contact-title"
            >
              {t.contact.title}
            </h2>
          </div>
          <div className="col-span-12 md:col-span-7 md:col-start-6 flex items-end">
            <p className="text-lg text-ink-soft font-light max-w-xl" data-testid="contact-kicker">
              {t.contact.kicker}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-12 gap-y-16 md:gap-x-16">
          {/* Form */}
          <div className="col-span-12 md:col-span-7">
            <form
              ref={formRef}
              className={`${revealClass(formVisible)} space-y-8`}
              onSubmit={submit}
              data-testid="contact-form"
              noValidate
            >
              <div>
                <label
                  htmlFor="name"
                  className="eyebrow block mb-2"
                  data-testid="contact-label-name"
                >
                  {t.contact.name}
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  required
                  autoComplete="name"
                  value={data.name}
                  onChange={handleChange}
                  className="editorial-input"
                  data-testid="contact-input-name"
                />
              </div>

              <div>
                <label
                  htmlFor="email"
                  className="eyebrow block mb-2"
                  data-testid="contact-label-email"
                >
                  {t.contact.email}
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  autoComplete="email"
                  value={data.email}
                  onChange={handleChange}
                  className="editorial-input"
                  data-testid="contact-input-email"
                />
              </div>

              <div>
                <label
                  htmlFor="message"
                  className="eyebrow block mb-2"
                  data-testid="contact-label-message"
                >
                  {t.contact.message}
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  rows={5}
                  value={data.message}
                  onChange={handleChange}
                  className="editorial-input resize-none"
                  data-testid="contact-input-message"
                />
              </div>

              <div className="flex flex-col sm:flex-row sm:items-center gap-6 pt-4">
                <button
                  type="submit"
                  disabled={status.state === "sending"}
                  data-testid="contact-submit"
                  className="group inline-flex items-center justify-center gap-3 bg-navy text-white px-8 py-4 text-[12px] tracking-[0.22em] uppercase font-medium hover:bg-navy-light transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {status.state === "sending" ? t.contact.sending : t.contact.send}
                  <ArrowRight
                    size={16}
                    className="transition-transform group-hover:translate-x-1"
                  />
                </button>

                {status.state !== "idle" && status.state !== "sending" && (
                  <p
                    data-testid="contact-status"
                    className={`text-sm ${
                      status.state === "success"
                        ? "text-navy"
                        : "text-red-700"
                    }`}
                  >
                    {status.message}
                  </p>
                )}
              </div>
            </form>
          </div>

          {/* Networks */}
          <aside className="col-span-12 md:col-span-5">
            <div className="md:border-l md:border-line md:pl-12">
              <p className="eyebrow mb-4">{t.contact.networks}</p>

              <div className="mb-10">
                <p className="text-xs uppercase tracking-[0.18em] text-ink-soft mb-3">
                  {t.contact.personal}
                </p>
                <div className="space-y-2">
                  {SOCIAL.personal.map((s) => {
                    const Icon = s.icon;
                    return (
                      <a
                        key={s.type}
                        href={s.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`contact-social-${s.type}`}
                        className="group flex items-center justify-between border-b border-line py-3 hover:border-ink transition-colors"
                      >
                        <span className="flex items-center gap-3 text-sm text-ink-soft group-hover:text-ink transition-colors">
                          <Icon size={15} strokeWidth={1.5} />
                          {s.label}
                        </span>
                      </a>
                    );
                  })}
                </div>
              </div>

              <div className="mb-10">
                <p className="text-xs uppercase tracking-[0.18em] text-ink-soft mb-3">
                  {t.contact.projectsLabel}
                </p>
                <div className="space-y-2">
                  {SOCIAL.projects.map((s) => {
                    const Icon = s.icon;
                    return (
                      <a
                        key={s.type}
                        href={s.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`contact-social-${s.type}`}
                        className="group flex items-center justify-between border-b border-line py-3 hover:border-ink transition-colors"
                      >
                        <span className="flex items-center gap-3 text-sm text-ink-soft group-hover:text-ink transition-colors">
                          <Icon size={15} strokeWidth={1.5} />
                          {s.label}
                        </span>
                      </a>
                    );
                  })}
                </div>
              </div>

              <div className="pt-2 space-y-3 text-sm text-ink-soft">
                <p className="flex items-center gap-3">
                  <MapPin size={15} strokeWidth={1.5} />
                  Girona, España
                </p>
                <p className="flex items-center gap-3">
                  <Globe size={15} strokeWidth={1.5} />
                  ivanbautista.es
                </p>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
};

export default Contact;
